import { perkpointApi } from "./client";

/** Every insights/dashboard response says which snapshot it came from. */
export type SnapshotMeta = {
  period: { days: number; from: string; to: string };
  snapshot_date: string;
  computed_at: string;
  run_id: string;
  stale: boolean;
};

export type InsightDays = 7 | 30 | 90;

/* ------------------------------------------------------------------ */
/* Dashboard (spec §11.4)                                              */
/* ------------------------------------------------------------------ */

export type ApiDashboard = SnapshotMeta & {
  summary: {
    total_members: number;
    new_this_month: number;
    total_members_change_pct: number | null;
    active_members: number;
    active_share_pct: number | null;
    active_change_pts: number | null;
    points_issued: number;
    points_issued_change_pct: number | null;
    points_redeemed: number;
    redeemed_share_of_issued_pct: number | null;
    outstanding_points: number;
    outstanding_liability: number;
    cpp: number;
    currency: string;
    liability_change_pct: number | null;
    campaigns_live: number;
    campaigns_ending_this_week: number;
  };
  enrolments_by_month: { month: string; value: number }[];
  points_by_source: { source: "base" | "bonus" | "campaign" | "manual"; points: number }[];
  tiers: { key: string; name: string; count: number; percent: number; upgrades: number; downgrades: number }[];
  segments: { key: string; name: string; color: string; count: number; pct: number; change_pct: number | null }[];
  needs_attention: { tag: "Budget" | "Tiers" | "Rules" | "Program"; title: string; description: string }[];
  campaign_performance: {
    campaign_id: string;
    campaign: string;
    channel: string;
    sent: number;
    read: number | null;
    converted: number | null;
    lift_pts: number | null;
  }[];
  recent_config_changes: {
    action: string;
    event: string;
    actor: string | null;
    role: string | null;
    at: string;
    object_type: string;
    object_name: string | null;
  }[];
};

/* ------------------------------------------------------------------ */
/* Member insights (spec §7.5)                                         */
/* ------------------------------------------------------------------ */

export type ApiInsights = SnapshotMeta & {
  period_markers: {
    total_members: number;
    total_members_change: number | null;
    active_members: number;
    active_members_change: number | null;
    points_earned: number;
    points_earned_change: number | null;
  };
  activity_trend: { month: string; active: number; earned: number; redeemed: number }[];
  customer_mix: { month: string; new_customers: number; repeat_customers: number }[];
  member_health: { key: "active" | "engaged" | "at_risk" | "inactive"; label: string; value: number; percent: number }[];
  health_rules: Record<string, number>;
  tier_distribution: { tier: string; key: string; members: number; percent: number }[];
  engagement: { key: string; label: string; count: number | null; percent: number | null }[];
  top_segments: { rank: number; key: string; name: string; members: number; engagement_change: number | null }[];
  attention_members: {
    member_id: string;
    external_member_id: string;
    name: string | null;
    email: string | null;
    tier: string | null;
    last_activity_at: string | null;
    points: number;
    engagement: string;
    status: string;
  }[];
  opportunities: { key: string; title: string; description: string }[];
};

/* ------------------------------------------------------------------ */
/* RFM segments (spec §7.4)                                            */
/* ------------------------------------------------------------------ */

export type ApiSegmentRules = {
  business_type: string;
  recent_days: number;
  lapsing_days: number;
  medium_frequency: number;
  high_frequency: number;
  medium_ticket: number;
  high_ticket: number;
  lookback_months?: number;
  updated_at?: string | null;
};

export type ApiRfmSegment = {
  id: string;
  key: string;
  label: string;
  type: "built_in" | "custom";
  color: string;
  members: number;
  share_pct: number;
  recency_max_days?: number;
  min_frequency?: number;
  min_ticket?: number;
};

export type ApiSegmentsOverview = {
  rules: ApiSegmentRules;
  presets: Record<string, Omit<ApiSegmentRules, "business_type">>;
  built_in: ApiRfmSegment[];
  custom_segments: ApiRfmSegment[];
  counts: Record<string, number>;
  total_members: number;
  computed_at: string | null;
  other_segments: { id: string; key: string; name: string; segment_type: string; kind: string; member_count: number }[];
};

/** The Segments screen's rule form, in the camelCase the API also accepts. */
export type SegmentRulesInput = {
  businessType: string;
  recentDays: number;
  lapsingDays: number;
  mediumFrequency: number;
  highFrequency: number;
  mediumTicket: number;
  highTicket: number;
};

export type CustomSegmentInput = {
  label: string;
  recencyMaxDays: number;
  minFrequency: number;
  minTicket: number;
};

export type InsightRange = { from: string; to: string };
export type InsightQuery = InsightDays | InsightRange;

export const insightKeys = {
  all: ["perkpoint", "insights"] as const,
  dashboard: (programId: string, period: InsightDays | string) =>
    ["perkpoint", "insights", programId, "dashboard", period] as const,
  insights: (programId: string, period: InsightDays | string) =>
    ["perkpoint", "insights", programId, "insights", period] as const,
  segments: (programId: string) => ["perkpoint", "insights", programId, "segments"] as const,
};

const base = (programId: string) => `/admin/programs/${programId}`;

function insightParams(query: InsightQuery) {
  return typeof query === "number" ? { days: query } : { from: query.from, to: query.to };
}

export const InsightsApi = {
  async dashboard(programId: string, query: InsightQuery = 30) {
    const { data } = await perkpointApi.get<ApiDashboard>(`${base(programId)}/dashboard`, {
      params: insightParams(query),
    });
    return data;
  },
  async exportDashboard(programId: string, query: InsightQuery = 30) {
    const { data } = await perkpointApi.get(`${base(programId)}/dashboard/export`, {
      params: insightParams(query),
      responseType: "blob",
    });
    return data as Blob;
  },
  async insights(programId: string, query: InsightQuery = 30) {
    const { data } = await perkpointApi.get<ApiInsights>(`${base(programId)}/insights`, {
      params: insightParams(query),
    });
    return data;
  },
  /** Recompute now (synchronous). 409 refresh_in_progress if one is already running. */
  async refresh(programId: string) {
    const { data } = await perkpointApi.post<{ status: string; computed_at: string; members_scored: number }>(
      `${base(programId)}/insights/refresh`,
    );
    return data;
  },
  /** Recent insight job runs, newest first. */
  async runs(programId: string, limit = 5) {
    const { data } = await perkpointApi.get<
      {
        id: string;
        trigger: "scheduled" | "manual" | "rule_change";
        status: string;
        snapshot_date: string;
        members_scored: number;
        error: string | null;
        started_at: string;
        finished_at: string | null;
      }[]
    >(`${base(programId)}/insights/runs`, { params: { limit } });
    return data;
  },
  async segments(programId: string) {
    const { data } = await perkpointApi.get<ApiSegmentsOverview>(`${base(programId)}/segments`);
    return data;
  },
  async saveSegmentRules(programId: string, rules: SegmentRulesInput) {
    const { data } = await perkpointApi.put<ApiSegmentsOverview>(`${base(programId)}/segments/rules`, rules);
    return data;
  },
  async addCustomSegment(programId: string, segment: CustomSegmentInput) {
    const { data } = await perkpointApi.post<ApiSegmentsOverview & { id: string }>(
      `${base(programId)}/segments/custom`,
      segment,
    );
    return data;
  },
  async removeCustomSegment(programId: string, segmentId: string) {
    const { data } = await perkpointApi.delete<ApiSegmentsOverview>(`${base(programId)}/segments/custom/${segmentId}`);
    return data;
  },
};

/** Download a Blob the API returned, under a file name. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** "2026-10" → "Oct". */
export function monthLabel(month: string) {
  return new Date(`${month}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short" });
}

/** Signed percent text, or "—" when the API has nothing to compare against. */
export function changeText(value: number | null | undefined, unit = "%") {
  if (value == null) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(1).replace(/\.0$/, "")}${unit}`;
}

/** Compact count: 184320 → "184.3K", 48200000 → "48.2M". */
export function compact(value: number) {
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (Math.abs(value) >= 1e4) return `${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return value.toLocaleString("en-IN");
}
